/**
 * @file tab-context.ts
 * @description «Магазин вкладки» (phase19.31, SC20–SC23, SC42).
 *
 * Каждая вкладка кабинета работает в своём кабинете (магазин или
 * платформа) и сообщает его серверу заголовком `X-Commora-Context` в
 * КАЖДОМ запросе; сервер проверяет, что контекст принадлежит сотруднику, и
 * выполняет запрос именно в нём. Сессия хранит только кабинет по
 * умолчанию — две вкладки разных магазинов больше не мешают друг другу.
 *
 * Где хранится: в памяти вкладки (переменная модуля), а не в localStorage —
 * общий для всех вкладок localStorage снова связал бы их между собой. После
 * перезагрузки вкладка выбирает кабинет заново по адресу (`/shop/:slug` или
 * `/platform`), поэтому сохранять значение между загрузками не нужно.
 *
 * Здесь только чистые правила (какой контекст подходит адресу, какие
 * запросы несут заголовок); выбирает контекст `RequireContextType`, хранит
 * в состоянии — `AuthProvider`.
 */

import type { AccountContext, ContextType, MeResponse } from "../auth/types";

/** Имя заголовка — контракт сервера (cabinet-api-contract.md §1). */
export const TAB_CONTEXT_HEADER = "X-Commora-Context";

/** Контекст этой вкладки; `null` — ещё не выбран (запросы идут по сессии). */
let tabContextId: string | null = null;

/** Текущий контекст вкладки. */
export function getTabContextId(): string | null {
  return tabContextId;
}

/** Запомнить контекст вкладки (до первого запроса страницы). */
export function setTabContextId(id: string | null): void {
  tabContextId = id;
}

/**
 * Нужен ли запросу заголовок. `/auth/*` (вход, выход, выбор кабинета по
 * умолчанию) работает по сессии, как раньше; исключение — `/auth/me`: с
 * заголовком сервер отдаёт кабинет этой вкладки.
 */
export function pathCarriesTabContext(path: string): boolean {
  const clean = path.split("?")[0] ?? "";
  if (clean === "/auth/me") return true;
  return !clean.startsWith("/auth/");
}

/** Заголовки вкладки для запроса по пути `path` (пустой объект — без заголовка). */
export function tabContextHeaders(path: string): Record<string, string> {
  if (!tabContextId || !pathCarriesTabContext(path)) return {};
  return { [TAB_CONTEXT_HEADER]: tabContextId };
}

/** Чего требует адрес страницы: кабинет платформы или конкретный магазин. */
export type TabContextTarget =
  | { type: "PLATFORM" }
  | { type: "SHOP"; slug: string };

/**
 * Контекст пользователя, подходящий адресу вкладки: для магазина — по slug
 * из адреса, для платформы — первый контекст платформы. `null` — у
 * пользователя нет доступа к этому кабинету.
 */
export function pickContextForTarget(
  contexts: readonly AccountContext[],
  target: TabContextTarget,
): AccountContext | null {
  if (target.type === "SHOP") {
    return contexts.find((c) => c.contextType === "SHOP" && c.shop?.slug === target.slug) ?? null;
  }
  return contexts.find((c) => c.contextType === ("PLATFORM" satisfies ContextType)) ?? null;
}

/**
 * Какой контекст должна получить вкладка по адресу. Во время входа под
 * сотрудником — только его кабинет (SC42: сервер отклонит любой другой), и
 * только если адрес ему соответствует; иначе `null` (страница уведёт в его
 * кабинет).
 */
export function desiredTabContext(me: MeResponse, target: TabContextTarget): AccountContext | null {
  if (me.impersonation) {
    const active = me.activeContext;
    if (!active) return null;
    return pickContextForTarget([active], target);
  }
  return pickContextForTarget(me.contexts, target);
}

/**
 * Ответ `/auth/me` с точки зрения вкладки: `activeContext` — кабинет вкладки,
 * если он есть среди кабинетов пользователя (сервер отдаёт то же самое на
 * запрос с заголовком; здесь — без ожидания ответа). Во время входа под
 * сотрудником ответ не меняется: кабинет — сотрудника.
 */
export function withTabContext(me: MeResponse, contextId: string | null): MeResponse {
  if (!contextId || me.impersonation || me.activeContext?.id === contextId) return me;
  const tabContext = me.contexts.find((c) => c.id === contextId);
  return tabContext ? { ...me, activeContext: tabContext } : me;
}
